import { Typography } from "@/components/atoms";
import { CurrencyIcon } from "@/components/atoms/icons/currencyicon";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { useUIColors, useWebViewport } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { BREAKPOINTS } from "@/core/utils/responsive";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  PanResponder,
  StyleSheet,
  View,
  type GestureResponderEvent,
  type PanResponderGestureState,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { usePropertyLabels } from "../hooks/usePropertyLabels";
import { formatPriceInMillions } from "../utils/formatPrice";

export interface PriceRangeSliderProps {
  value?: number;
  onValueChange?: (val: number) => void;
  /**
   * Sanctioned Iskan loan in AED (AC-05 / AC-06).
   * Shown fixed at top-right — does not change when the thumb moves.
   */
  loanAmountAed: number;
  /** Stretch to fill the parent instead of the fixed 570px design width (e.g. tablet). */
  fullWidth?: boolean;
  /** Stretch to match the status card column (content-driven height). */
  fillAvailableHeight?: boolean;
  /** Min height when side-by-side below the wide-desktop breakpoint (≤1320). */
  statusRowMinHeight?: number | undefined;
  /** width > 1320: compact content-hugging layout without minHeight floor. */
  compactStatusRow?: boolean;
  /** Natural content height (ignores forced minHeight / stretch). */
  onContentHeightChange?: ((height: number) => void) | undefined;
  /** Rendered under the slider, after a divider (e.g. unit search + bedrooms). */
  footer?: React.ReactNode;
  /** Square, borderless bottom edge so a separate card can continue it seamlessly. */
  attachedBelow?: boolean;
}

const SLIDER_MIN_M = 1;
/** Right end shows "3 M +"; the listing sends the no-limit max_price there. */
export const PRICE_SLIDER_MAX_M = 3;
const SLIDER_MAX_M = PRICE_SLIDER_MAX_M;
const SLIDER_SPAN_M = SLIDER_MAX_M - SLIDER_MIN_M;
const SLIDER_DEFAULT_M = SLIDER_MAX_M;
/** Ignore tiny float drift when comparing selection vs loan. */
const LOAN_COMPARE_EPSILON_AED = 500;
const LOAN_STATUS_PLACEHOLDER = "{{loanStatus}}";
/** 15 ticks between 1M–3M (no ticks at start/end); main ticks at 1.5M, 2M, 2.5M. */
const TICK_COUNT = 16;
const SLIDER_TICKS = Array.from({ length: TICK_COUNT - 1 }, (_, i) => ({
  ratio: (i + 1) / TICK_COUNT,
  isMain: (i + 1) % 4 === 0,
}));

function resolveLoanSelectionRelation(
  selectionAed: number,
  loanAmountAed: number,
): "above" | "within" {
  if (selectionAed > loanAmountAed + LOAN_COMPARE_EPSILON_AED) {
    return "above";
  }
  return "within";
}

function splitLoanEligibilityTemplate(template: string): {
  before: string;
  after: string;
} {
  const parts = template.split(LOAN_STATUS_PLACEHOLDER);
  if (parts.length < 2) {
    return { before: template, after: "" };
  }
  return {
    before: parts[0] ?? "",
    after: parts.slice(1).join(LOAN_STATUS_PLACEHOLDER),
  };
}

function clampSliderValue(value: number): number {
  if (value < SLIDER_MIN_M) {
    return SLIDER_MIN_M;
  }
  if (value > SLIDER_MAX_M) {
    return SLIDER_MAX_M;
  }
  return value;
}

function snapReleasedSliderValue(released: number): number {
  if (released >= SLIDER_MAX_M - 0.05) {
    return SLIDER_MAX_M;
  }
  if (released <= SLIDER_MIN_M + 0.05) {
    return SLIDER_MIN_M;
  }
  return released;
}

function positionToSliderValue(
  x: number,
  trackWidth: number,
  isRTL: boolean,
): number {
  if (trackWidth <= 0) {
    return SLIDER_MIN_M;
  }
  let ratio = x / trackWidth;
  if (ratio < 0) {
    ratio = 0;
  }
  if (ratio > 1) {
    ratio = 1;
  }
  if (isRTL) {
    ratio = 1 - ratio;
  }
  return SLIDER_MIN_M + ratio * SLIDER_SPAN_M;
}

function resolveLoanRatio(loanAmountAed: number): number {
  const loanMillions = Math.max(0, loanAmountAed) / 1_000_000;
  return Math.min(
    1,
    Math.max(0, (loanMillions - SLIDER_MIN_M) / SLIDER_SPAN_M),
  );
}

function normalizeLoanAmountAed(loanAmountAed: number): number {
  if (!Number.isFinite(loanAmountAed)) {
    return 0;
  }
  return Math.max(0, loanAmountAed);
}

function resolveLoanUi(
  selectionAed: number,
  loanAmountAed: number,
  labels: { loanStatusAbove: string; loanStatusWithin: string },
): { statusLabel: string; statusColor: string } {
  const relation = resolveLoanSelectionRelation(
    selectionAed,
    normalizeLoanAmountAed(loanAmountAed),
  );
  if (relation === "above") {
    return {
      statusLabel: labels.loanStatusAbove,
      statusColor: UI_PRIMITIVES.redDefault,
    };
  }
  return {
    statusLabel: labels.loanStatusWithin,
    statusColor: UI_PRIMITIVES.greenMedium,
  };
}

function buildContainerStyles(params: {
  fullWidth: boolean;
  fillAvailableHeight: boolean;
  compactStatusRow: boolean;
  statusRowMinHeight: number | undefined;
  isPhone: boolean;
  backgroundColor: string;
  borderColor: string;
}): StyleProp<ViewStyle> {
  const stylesList: StyleProp<ViewStyle>[] = [
    styles.container,
    {
      backgroundColor: params.backgroundColor,
      borderColor: params.borderColor,
    },
  ];
  if (params.fullWidth) {
    stylesList.push(styles.containerFullWidth);
  }
  if (params.fillAvailableHeight) {
    stylesList.push(styles.containerFillHeight);
  }
  if (params.fillAvailableHeight && params.compactStatusRow) {
    stylesList.push(styles.containerFillHeightCompact);
  }
  if (params.fillAvailableHeight && params.statusRowMinHeight != null) {
    stylesList.push({ minHeight: params.statusRowMinHeight });
  }
  if (params.isPhone) {
    stylesList.push(styles.containerPhone);
  }
  return stylesList;
}

function buildContentMeasureStyle(
  compactStatusRow: boolean,
  isPhone: boolean,
): StyleProp<ViewStyle> {
  const stylesList: StyleProp<ViewStyle>[] = [styles.contentMeasure];
  if (compactStatusRow) {
    stylesList.push(styles.contentMeasureCompact);
  }
  if (isPhone) {
    stylesList.push(styles.contentMeasurePhone);
  }
  return stylesList;
}

function formatMaxMillionLabel(maxMillion: string, isPhone: boolean): string {
  if (isPhone) {
    return `${maxMillion}+`;
  }
  return `${maxMillion} +`;
}

function buildFillStyle(
  slideRatio: number,
  fillColor: string,
  isRTL: boolean,
): StyleProp<ViewStyle> {
  const edge: ViewStyle = isRTL ? { right: 0 } : { left: 0 };
  return [
    styles.sliderFill,
    { backgroundColor: fillColor, width: `${slideRatio * 100}%` },
    edge,
  ];
}

function buildThumbStyle(
  slideRatio: number,
  thumbColor: string,
  isRTL: boolean,
): StyleProp<ViewStyle> {
  if (isRTL) {
    return [
      styles.thumb,
      { backgroundColor: thumbColor },
      { right: `${slideRatio * 100}%`, transform: [{ translateX: 8 }] },
    ];
  }
  return [
    styles.thumb,
    { backgroundColor: thumbColor },
    { left: `${slideRatio * 100}%`, transform: [{ translateX: -8 }] },
  ];
}

type SliderPanRefs = {
  valueRef: React.RefObject<number>;
  widthRef: React.RefObject<number>;
  startValueRef: React.RefObject<number>;
  positionToValueRef: React.RefObject<(x: number) => number>;
  pageToValueRef: React.RefObject<(pageX: number) => number | null>;
  isRTLRef: React.RefObject<boolean>;
  onValueChangeRef: React.RefObject<((val: number) => void) | undefined>;
  setCurrentValue: (value: number) => void;
};

/**
 * Pointer page X → slider value using the track's DOM rect. Pointer and rect are
 * both in visual px, so `html { zoom }` / cropped windows do not skew the mapping
 * (onLayout width and locationX can disagree under zoom).
 */
function pageXToSliderValue(
  pageX: number,
  node: View | null,
  isRTL: boolean,
): number | null {
  const withRect = node as (View & { getBoundingClientRect?: () => DOMRect }) | null;
  const rect = withRect?.getBoundingClientRect?.();
  if (!rect || rect.width <= 0 || !Number.isFinite(pageX)) {
    return null;
  }
  const scrollX = typeof window === "undefined" ? 0 : window.scrollX;
  return positionToSliderValue(pageX - scrollX - rect.left, rect.width, isRTL);
}

function createPriceRangePanResponder(refs: SliderPanRefs) {
  const applyValue = (newValue: number) => {
    refs.setCurrentValue(newValue);
    refs.valueRef.current = newValue;
  };

  return PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onStartShouldSetPanResponderCapture: () => true,
    onMoveShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponderCapture: () => true,
    onPanResponderTerminationRequest: () => false,
    onPanResponderGrant: (evt: GestureResponderEvent) => {
      const newValue =
        refs.pageToValueRef.current(evt.nativeEvent.pageX) ??
        refs.positionToValueRef.current(evt.nativeEvent.locationX);
      applyValue(newValue);
      refs.startValueRef.current = newValue;
    },
    onPanResponderMove: (
      _e: GestureResponderEvent,
      gestureState: PanResponderGestureState,
    ) => {
      const absoluteValue = refs.pageToValueRef.current(gestureState.moveX);
      if (absoluteValue != null) {
        applyValue(absoluteValue);
        return;
      }
      if (refs.widthRef.current === 0) {
        return;
      }
      let dx = gestureState.dx;
      if (refs.isRTLRef.current) {
        dx = -dx;
      }
      const valueDelta = (dx / refs.widthRef.current) * SLIDER_SPAN_M;
      applyValue(clampSliderValue(refs.startValueRef.current + valueDelta));
    },
    onPanResponderRelease: () => {
      const released = snapReleasedSliderValue(refs.valueRef.current);
      applyValue(released);
      refs.onValueChangeRef.current?.(released);
    },
  });
}

function usePriceRangeSliderDrag(
  value: number,
  isRTL: boolean,
  onValueChange: ((val: number) => void) | undefined,
) {
  const [width, setWidth] = useState(0);
  const [currentValue, setCurrentValue] = useState(value);

  const valueRef = useRef(currentValue);
  const widthRef = useRef(width);
  const startValueRef = useRef(currentValue);
  const isRTLRef = useRef(isRTL);
  const onValueChangeRef = useRef(onValueChange);
  const positionToValueRef = useRef((x: number) =>
    positionToSliderValue(x, widthRef.current, isRTLRef.current),
  );
  const trackRef = useRef<View>(null);
  const pageToValueRef = useRef((pageX: number) =>
    pageXToSliderValue(pageX, trackRef.current, isRTLRef.current),
  );

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    setCurrentValue(value);
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    isRTLRef.current = isRTL;
  }, [isRTL]);

  useEffect(() => {
    onValueChangeRef.current = onValueChange;
  }, [onValueChange]);

  useEffect(() => {
    positionToValueRef.current = (x: number) =>
      positionToSliderValue(x, widthRef.current, isRTLRef.current);
  });

  const panResponder = useRef(
    createPriceRangePanResponder({
      valueRef,
      widthRef,
      startValueRef,
      positionToValueRef,
      pageToValueRef,
      isRTLRef,
      onValueChangeRef,
      setCurrentValue,
    }),
  ).current;

  return {
    currentValue,
    slideRatio: (currentValue - SLIDER_MIN_M) / SLIDER_SPAN_M,
    panHandlers: panResponder.panHandlers,
    setTrackWidth: setWidth,
    trackRef,
  };
}

function PriceRangeTicks({
  loanAmountAed,
  isRTL,
  loanTickColor,
  defaultTickColor,
}: Readonly<{
  loanAmountAed: number;
  isRTL: boolean;
  loanTickColor: string;
  defaultTickColor: string;
}>) {
  const loanRatio = resolveLoanRatio(loanAmountAed);
  const halfTick = 1 / (TICK_COUNT * 2);

  return (
    <View style={styles.ticksContainer} pointerEvents="none">
      {SLIDER_TICKS.map((tick) => {
        const isLoanTick = Math.abs(tick.ratio - loanRatio) < halfTick;
        const visualLeft = isRTL ? 1 - tick.ratio : tick.ratio;
        return (
          <View
            key={tick.ratio}
            style={[
              styles.tick,
              {
                backgroundColor: isLoanTick ? loanTickColor : defaultTickColor,
                height: tick.isMain ? 10 : 6,
                left: `${visualLeft * 100}%`,
              },
            ]}
          />
        );
      })}
    </View>
  );
}

function SideMillionLabel({
  label,
  isPhone,
  color,
}: Readonly<{ label: string; isPhone: boolean; color: string }>) {
  const iconSize = isPhone ? 12 : 14;
  return (
    <View style={[styles.sideLabel, isPhone ? styles.sideLabelPhone : null]}>
      <View style={styles.sideLabelIcon}>
        <CurrencyIcon size={iconSize} color={color} />
      </View>
      <Typography
        color="primary"
        style={[
          styles.sideLabelText,
          isPhone ? styles.sideLabelTextPhone : null,
          { color },
        ]}
      >
        {label}
      </Typography>
    </View>
  );
}

function PriceRangeHeader({
  isPhone,
  isRTL,
  title,
  loanDisplayValue,
  loanAmountLabel,
  eligibilityBefore,
  eligibilityAfter,
  loanStatusLabel,
  loanStatusColor,
  titleColor,
}: Readonly<{
  isPhone: boolean;
  isRTL: boolean;
  title: string;
  loanDisplayValue: string;
  loanAmountLabel: string;
  eligibilityBefore: string;
  eligibilityAfter: string;
  loanStatusLabel: string;
  loanStatusColor: string;
  titleColor: string;
}>) {
  const eligibilityStyle = isPhone
    ? styles.eligibilityPhone
    : styles.eligibilityDesktop;

  return (
    <View style={[styles.header, isPhone ? styles.headerPhone : null]}>
      <View
        style={[styles.titleLoanRow, isRTL ? styles.titleLoanRowRTL : null]}
      >
        <Typography
          color="primary"
          style={[
            styles.boldText,
            isPhone ? styles.titlePhone : null,
            { color: titleColor },
          ]}
        >
          {title}
        </Typography>
        <Typography
          variant="footnote"
          color="primary"
          style={[
            styles.loanLine,
            isPhone ? styles.loanLinePhone : null,
            isRTL ? styles.loanLineRTL : null,
          ]}
        >
          <Typography
            variant="footnote"
            color="primary"
            style={styles.boldText}
          >
            {loanDisplayValue}
          </Typography>{" "}
          <Typography variant="footnote" color="tertiary">
            {loanAmountLabel}
          </Typography>
        </Typography>
      </View>
      <Typography
        variant="footnote"
        color="secondary"
        style={[eligibilityStyle, isRTL ? styles.eligibilityRTL : null]}
      >
        {eligibilityBefore}
        <Typography
          variant="footnote"
          style={[styles.boldText, { color: loanStatusColor }]}
        >
          {loanStatusLabel}
        </Typography>
        {eligibilityAfter}
      </Typography>
    </View>
  );
}

/** Labels at 25% / 50% / 75% of the track (the main ticks). */
function PriceRangeScaleLabels({
  isRTL,
  quarterLabel,
  midLabel,
  threeQuarterLabel,
}: Readonly<{
  isRTL: boolean;
  quarterLabel: string;
  midLabel: string;
  threeQuarterLabel: string;
}>) {
  const quarterLeft = isRTL ? "75%" : "25%";
  const threeQuarterLeft = isRTL ? "25%" : "75%";
  return (
    <View style={styles.labelsRow}>
      <Typography
        color="tertiary"
        style={[styles.scaleLabel, { left: quarterLeft }]}
      >
        {quarterLabel}
      </Typography>
      <Typography color="tertiary" style={[styles.scaleLabel, { left: "50%" }]}>
        {midLabel}
      </Typography>
      <Typography
        color="tertiary"
        style={[styles.scaleLabel, { left: threeQuarterLeft }]}
      >
        {threeQuarterLabel}
      </Typography>
    </View>
  );
}

function PriceRangeTrack({
  isRTL,
  loanAmountAed,
  slideRatio,
  trackColor,
  brandColor,
  panHandlers,
  onTrackLayout,
  trackRef,
}: Readonly<{
  isRTL: boolean;
  loanAmountAed: number;
  slideRatio: number;
  trackColor: string;
  brandColor: string;
  panHandlers: object;
  onTrackLayout: (width: number) => void;
  trackRef: React.RefObject<View | null>;
}>) {
  return (
    <View
      ref={trackRef}
      style={[
        styles.sliderArea,
        {
          cursor: "pointer",
          touchAction: "none",
          userSelect: "none",
        } as object,
      ]}
      onLayout={(e) => onTrackLayout(e.nativeEvent.layout.width)}
      {...panHandlers}
    >
      <View
        style={[styles.sliderTrack, { backgroundColor: trackColor }]}
        pointerEvents="none"
      />
      <PriceRangeTicks
        loanAmountAed={loanAmountAed}
        isRTL={isRTL}
        loanTickColor={brandColor}
        defaultTickColor={trackColor}
      />
      <View
        pointerEvents="none"
        style={buildFillStyle(slideRatio, brandColor, isRTL)}
      />
      <View
        pointerEvents="none"
        style={buildThumbStyle(slideRatio, brandColor, isRTL)}
      />
    </View>
  );
}

export function PriceRangeSlider({
  value = SLIDER_DEFAULT_M,
  onValueChange,
  loanAmountAed,
  fullWidth = false,
  fillAvailableHeight = false,
  statusRowMinHeight,
  compactStatusRow = false,
  onContentHeightChange,
  footer,
  attachedBelow = false,
}: Readonly<PriceRangeSliderProps>) {
  const c = useUIColors();
  const { isRTL } = useI18nContext();
  const { listing: LABELS } = usePropertyLabels();
  const { breakpointWidth } = useWebViewport();
  const isPhone = breakpointWidth < BREAKPOINTS.PHONE;
  const drag = usePriceRangeSliderDrag(value, isRTL, onValueChange);

  const loanUi = useMemo(
    () =>
      resolveLoanUi(drag.currentValue * 1_000_000, loanAmountAed, LABELS),
    [drag.currentValue, loanAmountAed, LABELS],
  );
  const eligibility = useMemo(
    () => splitLoanEligibilityTemplate(LABELS.loanEligibility),
    [LABELS.loanEligibility],
  );

  return (
    <View
      style={[
        buildContainerStyles({
          fullWidth,
          fillAvailableHeight,
          compactStatusRow,
          statusRowMinHeight,
          isPhone,
          backgroundColor: c.backgroundLevel1,
          borderColor: c.outlineInactive,
        }),
        footer ? styles.containerWithFooter : null,
        attachedBelow ? styles.containerAttachedBelow : null,
      ]}
    >
      <View
        style={buildContentMeasureStyle(compactStatusRow, isPhone)}
        onLayout={(event) => {
          const verticalPadding = compactStatusRow ? 24 : 32;
          onContentHeightChange?.(
            event.nativeEvent.layout.height + verticalPadding + 2,
          );
        }}
      >
        <PriceRangeHeader
          isPhone={isPhone}
          isRTL={isRTL}
          title={LABELS.priceRange}
          loanDisplayValue={formatPriceInMillions(loanAmountAed, isRTL)}
          loanAmountLabel={LABELS.loanAmount}
          eligibilityBefore={eligibility.before}
          eligibilityAfter={eligibility.after}
          loanStatusLabel={loanUi.statusLabel}
          loanStatusColor={loanUi.statusColor}
          titleColor={c.bloomRedDark}
        />

        <View style={[styles.sliderRow, isPhone ? styles.sliderRowPhone : null]}>
          <SideMillionLabel
            label={LABELS.oneMillion}
            isPhone={isPhone}
            color={c.fontPrimary}
          />
          <View style={styles.sliderContainer}>
            <PriceRangeTrack
              isRTL={isRTL}
              loanAmountAed={loanAmountAed}
              slideRatio={drag.slideRatio}
              trackColor={c.outlineInactive}
              brandColor={c.bloomRedDark}
              panHandlers={drag.panHandlers}
              onTrackLayout={drag.setTrackWidth}
              trackRef={drag.trackRef}
            />
            <PriceRangeScaleLabels
              isRTL={isRTL}
              quarterLabel={formatPriceInMillions(1_500_000, isRTL)}
              midLabel={LABELS.twoMillion}
              threeQuarterLabel={formatPriceInMillions(2_500_000, isRTL)}
            />
          </View>
          <SideMillionLabel
            label={formatMaxMillionLabel(LABELS.threeMillion, isPhone)}
            isPhone={isPhone}
            color={c.fontPrimary}
          />
        </View>
      </View>
      {footer ? (
        <>
          <View style={[styles.footerDivider, { backgroundColor: UI_PRIMITIVES.borderSlate }]} />
          {footer}
        </>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  /** Footer dropdowns overflow the card; every RN-web View is its own z-index:0 stacking context. */
  containerWithFooter: {
    zIndex: 3,
  },
  containerAttachedBelow: {
    borderBottomWidth: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  footerDivider: {
    height: 1,
    width: "100%",
  },
  container: {
    borderRadius: 12,
    borderWidth: 1,
    paddingTop: 16,
    paddingRight: 16,
    paddingBottom: 16,
    paddingLeft: 16,
    width: 570,
    gap: 16,
    maxWidth: "100%",
    flexGrow: 1,
    justifyContent: "center",
  },
  contentMeasure: {
    width: "100%",
    gap: 16,
  },
  contentMeasureCompact: {
    gap: 12,
  },
  contentMeasurePhone: {
    gap: 12,
  },
  containerFullWidth: {
    width: "100%",
    alignSelf: "stretch",
    maxWidth: "100%",
  },
  containerPhone: {
    paddingTop: 12,
    paddingRight: 12,
    paddingBottom: 12,
    paddingLeft: 12,
    gap: 12,
  },
  containerFillHeight: {
    flex: 1,
    alignSelf: "stretch",
    width: "100%",
    ...({
      display: "flex",
      flexDirection: "column",
      height: "100%",
      boxSizing: "border-box",
    } as object),
  },
  containerFillHeightCompact: {
    justifyContent: "flex-start",
    paddingTop: 12,
    paddingBottom: 12,
    gap: 12,
  },
  header: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 8,
    width: "100%",
  },
  headerPhone: {
    gap: 6,
  },
  titleLoanRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    width: "100%",
  },
  titleLoanRowRTL: {
    flexDirection: "row-reverse",
  },
  loanLine: {
    textAlign: "right",
    flexShrink: 1,
  },
  loanLinePhone: {
    textAlign: "right",
  },
  loanLineRTL: {
    textAlign: "left",
  },
  eligibilityDesktop: {
    textAlign: "right",
    alignSelf: "flex-end",
    maxWidth: "70%",
  },
  eligibilityPhone: {
    textAlign: "left",
    alignSelf: "stretch",
  },
  eligibilityRTL: {
    textAlign: "left",
    alignSelf: "flex-start",
  },
  boldText: {
    fontWeight: "700",
  },
  titlePhone: {
    fontSize: 16,
    lineHeight: 20,
    flexShrink: 0,
  },
  sliderRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    width: "100%",
  },
  sliderRowPhone: {
    gap: 6,
  },
  sideLabel: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
    height: 16,
    flexShrink: 0,
  },
  sideLabelPhone: {
    height: 14,
  },
  sideLabelIcon: {
    width: 14,
    height: 14,
    justifyContent: "center",
    alignItems: "center",
    transform: [{ translateY: -1 }],
  },
  sideLabelText: {
    fontFamily: "Matter-Medium",
    fontWeight: "500",
    fontSize: 14,
    lineHeight: 16,
    includeFontPadding: false,
    textAlignVertical: "center",
  },
  sideLabelTextPhone: {
    fontSize: 11,
    lineHeight: 14,
  },
  sliderContainer: {
    flex: 1,
    minWidth: 0,
  },
  sliderArea: {
    position: "relative",
    height: 26,
    justifyContent: "flex-start",
  },
  sliderTrack: {
    height: 6,
    width: "100%",
    borderRadius: 3,
    position: "absolute",
    top: 5,
  },
  ticksContainer: {
    position: "absolute",
    top: 16,
    left: 0,
    right: 0,
    height: 10,
  },
  tick: {
    position: "absolute",
    width: 1,
    marginLeft: -0.5,
    top: 0,
  },
  sliderFill: {
    height: 6,
    borderRadius: 3,
    position: "absolute",
    top: 5,
  },
  thumb: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: "#FFFFFF",
    position: "absolute",
    top: 0,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 3,
    justifyContent: "center",
    alignItems: "center",
    // @ts-ignore - cursor is supported on web but might not be in the RN typedefs
    cursor: "grab" as any,
  },
  labelsRow: {
    position: "relative" as const,
    height: 14,
    marginTop: 2,
  },
  scaleLabel: {
    position: "absolute" as const,
    fontFamily: "Matter-Regular",
    fontWeight: "400",
    fontSize: 8.8,
    ...({ transform: "translateX(-50%)" } as object),
  },
});
